import { ListChecks, Sparkles } from "lucide-react";

const DiscordIcon = ({ className }: { className?: string }) => (
	<svg
		viewBox="0 0 24 24"
		fill="currentColor"
		className={className}
		xmlns="http://www.w3.org/2000/svg"
		aria-label="Discord logo"
	>
		<title>Discord</title>
		<path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
	</svg>
);

export function HowItWorks() {
	const steps = [
		{
			icon: DiscordIcon,
			title: "Add Our Bot",
			description:
				"Simply add our Discord bot to your server with one click. No complex setup required.",
			color: "#0f9d8a",
		},
		{
			icon: Sparkles,
			title: "AI Analyzes Your Community",
			description:
				"Our AI agents automatically monitor conversations, engagement, and community health.",
			color: "#ff6b6b",
		},
		{
			icon: ListChecks,
			title: "Get Actionable Insights",
			description:
				"Receive insights and action items to improve your developer community.",
			color: "#ffc857",
		},
	];

	return (
		<section
			id="how-it-works"
			className="relative overflow-hidden py-24 sm:py-28"
		>
			<div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(15,157,138,0.18),_transparent_70%)]" />
			<div className="pointer-events-none absolute inset-y-0 right-0 -z-10 w-1/2 bg-[radial-gradient(circle_at_top_right,_rgba(77,185,255,0.2),_transparent_65%)] blur-2xl" />
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<div className="mx-auto mb-16 max-w-3xl text-center sm:mb-20">
					<div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/60 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground shadow-sm shadow-primary/10 backdrop-blur-xl">
						<span className="h-2 w-2 rounded-full bg-primary" />
						<span>Kickoff flow</span>
					</div>
					<h2 className="text-4xl sm:text-5xl">Get Started in Minutes</h2>
					<p className="mt-4 text-lg text-muted-foreground sm:text-xl">
						Add Squash, invite your core builders, and let our playful AI keep
						the vibe high while surfacing the signals you care about.
					</p>
				</div>

				<div className="grid gap-8 md:grid-cols-3 lg:gap-12">
					{steps.map((step, index) => (
						<div key={step.title} className="relative">
							<div className="group relative h-full overflow-hidden rounded-[28px] border border-white/60 bg-white/70 p-8 shadow-lg shadow-primary/5 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-primary/20">
								<div className="absolute -right-10 -top-12 h-32 w-32 rounded-full bg-[radial-gradient(circle,_rgba(15,157,138,0.15),_transparent_70%)] blur-2xl transition-all group-hover:scale-125" />
								<div className="relative flex flex-col items-center text-center">
									<div
										className="mb-4 flex h-20 w-20 items-center justify-center rounded-2xl shadow-lg shadow-primary/20"
										style={{ backgroundColor: step.color }}
									>
										<step.icon className="h-10 w-10 text-white" />
									</div>
									<div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
										<span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
											{index + 1}
										</span>
										<span>Step {index + 1}</span>
									</div>
									<h3 className="text-2xl text-foreground">{step.title}</h3>
									<p className="mt-3 text-sm text-muted-foreground sm:text-base">
										{step.description}
									</p>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
